import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CANCELLATION_REASONS = [
  { value: "price_too_high", label: "Price too high" },
  { value: "client_ghosted", label: "Client ghosted" },
  { value: "client_changed_plans", label: "Client changed plans" },
  { value: "client_chose_competitor", label: "Client chose competitor" },
  { value: "client_rejected_offer", label: "Client rejected offer" },
  { value: "client_chose_different_destination", label: "Client chose a different destination" },
  { value: "other", label: "Other" },
] as const;

interface CancelLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string, details?: string) => void;
}

export function CancelLeadDialog({
  open,
  onOpenChange,
  onConfirm,
}: CancelLeadDialogProps) {
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [otherDetails, setOtherDetails] = useState("");
  const [error, setError] = useState("");

  const handleConfirm = () => {
    if (!selectedReason) {
      setError("Please select a cancellation reason");
      return;
    }

    if (selectedReason === "other" && !otherDetails.trim()) {
      setError("Please provide details for the cancellation");
      return;
    }

    const reasonLabel = CANCELLATION_REASONS.find(r => r.value === selectedReason)?.label || selectedReason;
    const fullReason = selectedReason === "other" 
      ? `Other: ${otherDetails.trim()}`
      : reasonLabel;

    onConfirm(fullReason, selectedReason === "other" ? otherDetails.trim() : undefined);
    resetForm();
    onOpenChange(false);
  };

  const handleCancel = () => {
    resetForm();
    onOpenChange(false);
  };

  const resetForm = () => {
    setSelectedReason("");
    setOtherDetails("");
    setError("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Reason for Cancelling Lead</DialogTitle>
          <DialogDescription>
            Please select why this lead is being cancelled. This will be recorded in the audit trail for analytics.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reason">Cancellation Reason *</Label>
            <Select
              value={selectedReason}
              onValueChange={(value) => {
                setSelectedReason(value);
                setError("");
                if (value !== "other") {
                  setOtherDetails("");
                }
              }}
            >
              <SelectTrigger className={error && !selectedReason ? "border-destructive" : ""}>
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                {CANCELLATION_REASONS.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedReason === "other" && (
            <div className="space-y-2">
              <Label htmlFor="details">Please specify *</Label>
              <Textarea
                id="details"
                value={otherDetails}
                onChange={(e) => {
                  setOtherDetails(e.target.value);
                  setError("");
                }}
                placeholder="Enter the reason for cancellation..."
                rows={3}
                maxLength={500}
                className={error && selectedReason === "other" && !otherDetails.trim() ? "border-destructive" : ""}
              />
              <div className="flex justify-end text-xs text-muted-foreground">
                <span>{otherDetails.length}/500</span>
              </div>
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm}
            variant="destructive"
          >
            Confirm Cancellation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
